import { Message } from '@/store/useChatStore';

export const NO_RESPONSE_DELIVERY_ERROR =
  'No response received. Your message may not have been delivered. Please try again.';

export const STREAM_INTERRUPTED_ERROR =
  'The reply to this message did not finish. Tap Retry response to send it again.';

export const INTERRUPTED_RESPONSE_ERROR = STREAM_INTERRUPTED_ERROR;

/** Shown in the leave-generation modal. */
export const GENERATION_IN_PROGRESS_WARNING =
  'LiFT is still thinking or generating your response. If you refresh or leave now, that reply will be lost. Stay on this page to let it finish — or return afterward and tap Retry response.';

export interface ApiPost {
  id?: number;
  text: string;
  text_src?: string;
  created_at?: string;
}

export function mapPostsToMessages(posts: ApiPost[]) {
  return posts.map((post) => ({
    id: String(post.id),
    text: post.text,
    isUser: post.text_src?.toLowerCase() === 'user',
    timestamp: post.created_at ?? new Date().toISOString(),
  }));
}

/** Last persisted turn has no assistant reply — generation was interrupted. */
export function getInterruptedUserMessageId(
  messages: Pick<Message, 'id' | 'isUser'>[]
): string | null {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage?.isUser) return null;
  return lastMessage.id;
}

export function getPostsReconciliationKey(
  practiceId: string | null,
  posts: ApiPost[]
): string {
  return `${practiceId ?? 'unknown'}:${posts.map((post) => post.id).join(',')}`;
}

/** True when the latest persisted post is an assistant reply. */
export function hasPersistedAssistantReply(posts: ApiPost[]): boolean {
  if (posts.length === 0) {
    return false;
  }

  return posts[posts.length - 1]?.text_src?.toLowerCase() !== 'user';
}

export function countLocalUserTurns(
  messages: Pick<Message, 'isUser'>[]
): number {
  return messages.filter((message) => message.isUser).length;
}

export function countPersistedUserTurns(posts: ApiPost[]): number {
  return posts.filter((post) => post.text_src?.toLowerCase() === 'user').length;
}

/**
 * The server has saved a reply to the newest local user turn: the posts end on
 * an assistant reply and hold at least as many user turns as the chat shows.
 * A reply to an earlier turn alone does not count.
 */
export function hasPersistedReplyForLatestTurn(
  localMessages: Pick<Message, 'isUser'>[],
  posts: ApiPost[]
): boolean {
  if (!hasPersistedAssistantReply(posts)) {
    return false;
  }

  return countPersistedUserTurns(posts) >= countLocalUserTurns(localMessages);
}

/**
 * Avoid wiping locally rendered turns when server posts lag behind or are empty.
 * Used for passive posts-query sync and explicit stream recovery.
 */
/** Local UI already shows a finished assistant reply matching persisted posts. */
export function isLocalConversationComplete(
  localMessages: Pick<Message, 'isUser' | 'text'>[],
  posts: ApiPost[]
): boolean {
  if (localMessages.length === 0 || !hasPersistedAssistantReply(posts)) {
    return false;
  }

  const lastLocal = localMessages[localMessages.length - 1];
  if (!lastLocal || lastLocal.isUser || !lastLocal.text.trim()) {
    return false;
  }

  return countLocalUserTurns(localMessages) <= countPersistedUserTurns(posts);
}

export function shouldRebuildFromServerPosts(
  localMessages: Pick<Message, 'isUser' | 'text'>[],
  posts: ApiPost[],
  options?: { forRecovery?: boolean }
): boolean {
  if (posts.length === 0) {
    return localMessages.length === 0;
  }

  // Nothing rendered locally (e.g. reopening a past session): stored history
  // always wins, even when it ends on an unanswered user turn — the caller
  // marks that turn as interrupted.
  if (!options?.forRecovery && localMessages.length === 0) {
    return true;
  }

  if (!options?.forRecovery && !hasPersistedAssistantReply(posts)) {
    return false;
  }

  if (options?.forRecovery && !hasPersistedAssistantReply(posts)) {
    return false;
  }

  const localUserTurns = countLocalUserTurns(localMessages);
  const serverUserTurns = countPersistedUserTurns(posts);

  if (localUserTurns > serverUserTurns) {
    return false;
  }

  const lastLocal = localMessages[localMessages.length - 1];
  if (
    lastLocal &&
    !lastLocal.isUser &&
    lastLocal.text.trim().length > 0 &&
    !hasPersistedAssistantReply(posts)
  ) {
    return false;
  }

  return true;
}

export const DEFAULT_STREAM_RECOVERY_DELAYS_MS = [1500, 2500, 4000];

/**
 * After a response timeout the client has already waited the full timeout,
 * so one quick check for a saved reply is enough.
 */
export const AFTER_TIMEOUT_RECOVERY_DELAYS_MS = [0];

/** Soul Map turns can take minutes to persist — poll longer before giving up. */
export const SOULMAP_STREAM_RECOVERY_DELAYS_MS = [
  3000, 5000, 8000, 12000, 15000,
];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Poll posts until the server has persisted an assistant reply, or give up. */
export async function refetchUntilAssistantReply(
  refetch: () => Promise<{ data?: { results?: ApiPost[] } | undefined }>,
  options?: {
    delaysMs?: number[];
    shouldAbort?: () => boolean;
    /** Defaults to any assistant reply being last. */
    isAnswered?: (posts: ApiPost[]) => boolean;
  }
): Promise<ApiPost[] | null> {
  const isAnswered = options?.isAnswered ?? hasPersistedAssistantReply;
  const delaysMs = options?.delaysMs ?? DEFAULT_STREAM_RECOVERY_DELAYS_MS;

  for (const delayMs of delaysMs) {
    if (options?.shouldAbort?.()) {
      return null;
    }

    await sleep(delayMs);

    if (options?.shouldAbort?.()) {
      return null;
    }

    const result = await refetch();
    const posts = result.data?.results ?? [];

    if (isAnswered(posts)) {
      return posts;
    }
  }

  return null;
}

/** Map websocket errors to user-facing copy based on whether streaming had started. */
export function resolveStreamErrorMessage(
  error: string,
  hasPartialContent: boolean
): string {
  if (hasPartialContent) {
    return STREAM_INTERRUPTED_ERROR;
  }

  if (error.includes('No response received') || error.includes('timed out')) {
    return NO_RESPONSE_DELIVERY_ERROR;
  }

  return error || NO_RESPONSE_DELIVERY_ERROR;
}

/** Server error codes for a turn the server gave up on (LE-338). */
export const SERVER_REPLY_FAILURE_CODES = new Set([
  'empty_response',
  'generation_failed',
  'generation_timeout',
  'save_failed',
  'turn_failed',
]);

export const REPLY_FAILED_ERROR =
  "The reply didn't come through. Tap Retry to try again.";

/**
 * The text shown under a user message whose turn failed. A failure the server
 * reported gets a plain explanation instead of the raw server error.
 */
export function resolveTurnErrorMessage(
  error: string,
  hasPartialContent: boolean,
  details?: { code?: string }
): string {
  if (
    !hasPartialContent &&
    details?.code &&
    SERVER_REPLY_FAILURE_CODES.has(details.code)
  ) {
    return REPLY_FAILED_ERROR;
  }
  return resolveStreamErrorMessage(error, hasPartialContent);
}
